import { FC, useEffect, useState } from "react";
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { PrimaryButton, SecondaryButton } from "../components/Buttons";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import HorizontalPadding from "../components/HorizontalPadding";
import { clearDispatchValues } from "../store/slices/dispatch";
import { useNavigate } from "react-router-dom";
import DispatchDraftTable from "./Manifest/DispatchDraftTable";
import DispatchGeneratedTable from "./Manifest/DispatchGeneratedTable";
import Modal from "../components/Modal";
import {
  ManifestHeaderInterface,
  ManifestStatus,
  ManifestType,
} from "../interfaces/ManifestInterface";
import { getManifestPostponed, updateManifestStatus } from "../services/manifestServices";
import moment from "moment";
import { alertService, loaderService } from "../services";

const Dispatch: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const loading = loaderService.useIsLoading();
  const user = useAppSelector((state) => state.user);

  const [loadingDispatches, setLoadingDispatches] = useState(true);
  const [openNewDispatchWarning, setOpenNewDispatchWarning] = useState(false);
  const [postponedDispatchList, setPostponedDispatchList] = useState<
    ManifestHeaderInterface[]
  >([]);

  const handleNewManifestClick = () => {
    if (postponedDispatchList.length > 0) {
      setOpenNewDispatchWarning(true);
      return;
    }

    dispatch(clearDispatchValues());
    navigate("/dispatch/create");
  };

  const handleNewManifest = async () => {
    const userLogin = user.user?.userLogin;

    if (!userLogin) {
      alertService.error(
        "No hay un nombre de usuario asociado a la sesión actual"
      );
      return;
    }

    for (const dispatch of postponedDispatchList) {
      const masterShipmentHeaderID = dispatch.masterShipmentHeaderID;

      if (!masterShipmentHeaderID) {
        continue;
      }

      const response = await updateManifestStatus(
        masterShipmentHeaderID,
        ManifestStatus.INACTIVO,
        userLogin
      );
  
      if (response.didError) {
        alertService.error(
          `Hubo un error anulando el manifiesto ${dispatch.masterShipmentHeaderCode}`,
          response.errorMessage
        );
        return;
      }
    }

    dispatch(clearDispatchValues());
    navigate("/dispatch/create");
  }

  useEffect(() => {
    const buCode = user.businessUnit?.code;

    if (!buCode) {
      return;
    }

    const afunction = async () => {
      const postponedDispatchList = await getManifestPostponed(
        ManifestType.DESPACHO,
        moment().subtract(3, "days").toDate(),
        moment().toDate(),
        buCode
      );

      if (postponedDispatchList.didError || !postponedDispatchList.model) {
        setLoadingDispatches(false);
        return;
      }

      setPostponedDispatchList(
        postponedDispatchList.model.filter(
          (m) => m.statusID === ManifestStatus.POSTERGADO
        )
      );
      setLoadingDispatches(false);
    };

    afunction();
  }, [user]);

  return (
    <main className="lg:pl-72 pb-32">
      <div className="py-6 sm:px-6 lg:px-8 bg-white relative flex items-center justify-between h-32">
        <header className="ml-4 text-2xl font-bold text-gray-700 ">
          Manifiesto de Despacho
        </header>

        <PrimaryButton
          disabled={loading || loadingDispatches}
          onClick={handleNewManifestClick}
          className="pr-4"
        >
          <DocumentTextIcon
            className="h-5 w-5 flex-none text-gray-200 mr-1"
            aria-hidden="true"
          />
          Nuevo
        </PrimaryButton>
      </div>

      <HorizontalPadding paddingTop>
        <div className="flex flex-col w-full gap-12">
          <DispatchDraftTable
            loadingDispatches={loadingDispatches}
            postponedDispatchList={postponedDispatchList}
          />

          <DispatchGeneratedTable />
        </div>
      </HorizontalPadding>

      <Modal
        openModal={openNewDispatchWarning}
        setOpenModal={setOpenNewDispatchWarning}
      >
        <div className="flex flex-col gap-4 max-w-md mx-auto items-center">
          <div className="flex flex-col items-center">
            <ExclamationTriangleIcon className="text-orange-500 h-28 w-28 mx-auto opacity-90" />
            <h2 className="text-xl font-semibold leading-7 text-gray-700 text-center mt-2">
              Crear nuevo despacho
            </h2>
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-gray-600 text-center whitespace-pre-line">
              Ya existen despachos postergados, crear uno nuevo anulará los
              existentes ¿Estás seguro de que quieres continuar con el proceso?
            </p>
          </div>

          <div className="flex w-full flex-row gap-3 items-center justify-between mt-4">
            <SecondaryButton
              onClick={() => setOpenNewDispatchWarning(false)}
              className="px-4 py-2"
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton
              onClick={async () => {
                loaderService.start();
                await handleNewManifest();
                loaderService.stop();
                setOpenNewDispatchWarning(false);
              }}
              className="px-4 py-2"
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default Dispatch;
